import { z } from 'zod';

export const nullablePositiveNumberSchema = z
  .union([z.literal(''), z.coerce.number().min(0)])
  .transform(value => (value === '' ? null : value));

export const toNullableTrimmedString = (value?: string | null) =>
  value?.trim() || null;

export const toFormNumberValue = (value?: number | null) =>
  value == null ? '' : String(value);

/** Whether a typed-in value reads as a number of zero or more. */
export const isNonNegativeNumberString = (value: string) => {
  const parsed = Number(value);

  return !Number.isNaN(parsed) && parsed >= 0;
};

/** Whether a typed-in value reads as a finite number, negative included. */
export const isNumberString = (value: string) => Number.isFinite(Number(value));

/** A required text input holding any number, negative included, parsed to a number. */
export const requiredNumberStringSchema = (message: string) =>
  z
    .string()
    .trim()
    .min(1, { message })
    .refine(isNumberString, { message })
    .transform(Number);

/**
 * An optional text input holding any number, negative included, parsed to a
 * number. An empty input becomes `undefined`.
 */
export const optionalNumberStringSchema = (message: string) =>
  z
    .string()
    .trim()
    .refine(value => !value || isNumberString(value), { message })
    .transform(value => (value ? Number(value) : undefined));

/**
 * A required text input holding a number of zero or more, parsed to a number.
 *
 * @param message - Shown when the input is empty.
 * @param invalidMessage - Shown when the input is not a number of zero or more.
 */
export const requiredNonNegativeNumberStringSchema = (
  message: string,
  invalidMessage = message
) =>
  z
    .string()
    .trim()
    .min(1, { message })
    .refine(isNonNegativeNumberString, { message: invalidMessage })
    .transform(Number);

/**
 * An optional text input holding a number of zero or more, parsed to a number.
 * An empty input becomes `undefined`.
 *
 * @param message - Shown when the input is not a number of zero or more.
 */
export const optionalNonNegativeNumberStringSchema = (message: string) =>
  z
    .string()
    .trim()
    .refine(value => !value || isNonNegativeNumberString(value), { message })
    .transform(value => (value ? Number(value) : undefined));
